import React, { useRef, useState, useEffect } from 'react';

interface CarouselProps {
  children: React.ReactNode[] | React.ReactNode;
  itemClassName?: string;
  gap?: number;
}

const Carousel: React.FC<CarouselProps> = ({ children, itemClassName = '', gap = 16 }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Default responsive sizes for slides (can be overridden via `itemClassName` prop)
  const defaultItemClasses = 'min-w-[220px] sm:min-w-[300px] md:min-w-[420px] lg:min-w-[520px]';

  const updateArrows = () => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth + 10 < el.scrollWidth);
  };

  useEffect(() => {
    updateArrows();
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);

  const scrollBy = (dir: 'left' | 'right') => {
    const el = containerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    const to = dir === 'left' ? el.scrollLeft - amount : el.scrollLeft + amount;
    el.scrollTo({ left: to, behavior: 'smooth' });
  };

  const items = Array.isArray(children) ? children : [children];

  return (
    <div className="relative">
      {/* Arrows */}
      <button
        aria-hidden={!canScrollLeft}
        onClick={() => scrollBy('left')}
        className={`absolute z-20 left-2 top-1/2 -translate-y-1/2 p-2 rounded-full shadow-md transition-opacity bg-white/90 dark:bg-slate-800/90 ${
          canScrollLeft ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}
      >
        ‹
      </button>

      <div
        ref={containerRef}
        className="overflow-x-auto scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <div className="flex items-stretch" style={{ gap: gap }}>
          {items.map((child, idx) => (
            <div key={idx} className={`${itemClassName || defaultItemClasses} flex-shrink-0`}>
              {child}
            </div>
          ))}
        </div>
      </div>

      <button
        aria-hidden={!canScrollRight}
        onClick={() => scrollBy('right')}
        className={`absolute z-20 right-2 top-1/2 -translate-y-1/2 p-2 rounded-full shadow-md transition-opacity bg-white/90 dark:bg-slate-800/90 ${
          canScrollRight ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}
      >
        ›
      </button>
    </div>
  );
};

export default Carousel;
